import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, UserPlus, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { usersApi, usersKeys } from '@/lib/api';
import { UserRole, type AzureAdUser, type CreateUserRequest } from '@/types';

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateUserRequest) => void;
  isSubmitting?: boolean | undefined;
}

const ROLE_OPTIONS: { value: UserRole; label: string; description: string }[] = [
  {
    value: 'MigrationAdmin',
    label: 'Admin',
    description: 'Full access to all features including settings',
  },
  {
    value: 'MigrationOperator',
    label: 'Operator',
    description: 'Can start and manage migrations',
  },
  {
    value: 'MigrationViewer',
    label: 'Viewer',
    description: 'Read-only access to view migrations',
  },
];

export function AddUserDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: AddUserDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<AzureAdUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>('MigrationViewer');

  // Search Azure AD users
  const {
    data: searchResults,
    isLoading: isSearching,
    isFetching,
  } = useQuery({
    queryKey: usersKeys.azureAdSearch(searchQuery),
    queryFn: () => usersApi.searchAzureAd(searchQuery),
    enabled: searchQuery.length >= 3,
    staleTime: 30000,
  });

  const handleSubmit = useCallback(() => {
    if (!selectedUser) return;

    onSubmit({
      azureAdObjectId: selectedUser.id,
      email: selectedUser.email,
      displayName: selectedUser.displayName,
      role: selectedRole,
    });
  }, [selectedUser, selectedRole, onSubmit]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    setSelectedUser(null);
    setSelectedRole('MigrationViewer');
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add User
          </DialogTitle>
          <DialogDescription>
            Search for a user in Azure AD and assign them a role.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Search */}
          <div className="space-y-1.5">
            <Label>Search Azure AD</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedUser(null);
                }}
                className="pl-9"
              />
            </div>
            {searchQuery.length > 0 && searchQuery.length < 3 && (
              <p className="text-xs text-muted-foreground">
                Enter at least 3 characters to search
              </p>
            )}
          </div>

          {/* Search Results */}
          {searchQuery.length >= 3 && (
            <div className="space-y-1.5">
              {isSearching || isFetching ? (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : searchResults && searchResults.length > 0 ? (
                <div className="max-h-[180px] overflow-y-auto space-y-1.5 pb-1">
                  {searchResults.map((user) => (
                    <Card
                      key={user.id}
                      className={`cursor-pointer transition-colors ${
                        selectedUser?.id === user.id
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setSelectedUser(user)}
                    >
                      <CardContent className="p-2.5">
                        <p className="text-sm font-medium">{user.displayName}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                        {user.jobTitle && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {user.jobTitle}
                            {user.department && ` • ${user.department}`}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-muted-foreground py-3">
                  No users found matching &quot;{searchQuery}&quot;
                </p>
              )}
            </div>
          )}

          {/* Selected User */}
          {selectedUser && (
            <div className="px-3 py-2 border rounded-lg bg-muted/30">
              <p className="text-xs text-muted-foreground">Selected User</p>
              <p className="text-sm font-medium">{selectedUser.displayName}</p>
              <p className="text-xs text-muted-foreground">{selectedUser.email}</p>
            </div>
          )}

          {/* Role Selection */}
          <div className="space-y-1.5">
            <Label>Assign Role</Label>
            <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as UserRole)}>
              <SelectTrigger>
                <SelectValue>
                  {ROLE_OPTIONS.find((o) => o.value === selectedRole)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="font-medium">{option.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{option.description}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedUser || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
